import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RolesService } from './roles.service';
import { LayersUnsavedChangesService } from './layers-unsaved-changes.service';

@Injectable({
  providedIn: 'root'
})
export class RoleManagementNavService implements OnDestroy {
  private navigationStack: string[] = ['/rolemanagement/roles'];
  private readonly stackUpdated$ = new BehaviorSubject<string[]>([...this.navigationStack]);
  private readonly navigationSubscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly rolesService: RolesService,
    private readonly layersUnsavedChangesService: LayersUnsavedChangesService
  ) {
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects.split('?')[0];
        if (this.isRoleManagementUrl(url)) {
          this.pushNavigation(url);
        }
      });
  }

  /**
   * Push a new URL to the navigation stack
   */
  pushNavigation(url: string): void {
    const existingIndex = this.navigationStack.lastIndexOf(url);
    if (existingIndex === this.navigationStack.length - 1) {
      return;
    }

    if (existingIndex >= 0) {
      this.navigationStack = this.navigationStack.slice(0, existingIndex + 1);
    } else {
      this.navigationStack.push(url);
    }

    this.stackUpdated$.next([...this.navigationStack]);
  }

  /**
   * Navigate back using the stack
   */
  navigateBack(): void {
    if (this.navigationStack.length <= 1) { return; }

    this.layersUnsavedChangesService.confirmLeaveIfDirty().subscribe(canLeave => {
      if (!canLeave) { return; }
      this.navigationStack.pop();
      const previousUrl = this.navigationStack.at(-1);
      if (previousUrl) {
        this.router.navigateByUrl(previousUrl);
      }
      this.stackUpdated$.next([...this.navigationStack]);
    });
  }

  navigateHome(): void {
    this.layersUnsavedChangesService.confirmLeaveIfDirty().subscribe(canLeave => {
      if (!canLeave) { return; }
      this.rolesService.setSelectedRole(null);
      this.resetStack();
      this.router.navigate(['/rolemanagement/roles']);
    });
  }

  /**
   * Get current navigation stack
   */
  getStack(): string[] {
    return [...this.navigationStack];
  }

  /**
   * Get stack updates as observable
   */
  getStackUpdates$(): Observable<string[]> {
    return this.stackUpdated$.asObservable();
  }

  /**
   * Reset stack to root
   */
  resetStack(): void {
    this.navigationStack = ['/rolemanagement/roles'];
    this.stackUpdated$.next([...this.navigationStack]);
  }

  /**
   * Check if at root
   */
  isAtRoot(): boolean {
    return this.navigationStack.length === 1;
  }

  ngOnDestroy(): void {
    this.navigationSubscription.unsubscribe();
  }

  private isRoleManagementUrl(url: string): boolean {
    return url === '/rolemanagement/roles'
      || url.startsWith('/rolemanagement/roles/')
      || url === '/rolemanagement/manage-permissions'
      || url.startsWith('/rolemanagement/manage-permissions/');
  }
}
